/**
 * Resolves the native control behind a Modus `inputChange`-style `CustomEvent`:
 * prefers `detail.target` (the real `<input>`/`<select>`), falls back to
 * `event.target`, then narrows to `ctor` (`null` if it doesn't match).
 *
 * @example
 * const target = getModusEventTarget(event, HTMLSelectElement);
 * if (!target) return;
 */
export const getModusEventTarget = <T extends HTMLElement>(
  event: CustomEvent,
  ctor: abstract new (...args: never[]) => T
): T | null => {
  const detail: unknown = event.detail;
  const detailTarget =
    detail !== null && typeof detail === "object" && "target" in detail
      ? detail.target
      : null;
  // Only prefer detail.target when it's an element; otherwise fall back to
  // event.target so a null/non-element detail.target doesn't drop the event.
  const target =
    detailTarget instanceof HTMLElement ? detailTarget : event.target;
  return target instanceof ctor ? target : null;
};
